import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMetaPixel, getActiveCtaPixelIds } from '../hooks/useMetaPixel';
import { LockIcon, HeartIcon, MessageCircleIcon, BookmarkIcon, ImageIcon, VideoIcon, BadgeCheckIcon, ShieldAlertIcon, ChevronLeftIcon, Share2Icon } from 'lucide-react';

/**
 * /quiz — age-gated, branching qualifier that runs BEFORE the lander.
 *
 * Presentation: a locked creator feed (GlykoFans) sits behind a modal. The age
 * check pops immediately on load; every following question reuses the same
 * modal. No progress bar / step counter is shown, so the visitor never sees how
 * many questions remain.
 *
 * Flow: age check -> Q1 (the fork) -> Q2 -> Q3 confirmation.
 * Anyone who answers "no" to Q1 is sent straight to the exclusive-content URL;
 * everyone else continues and lands on the presentation (/demo).
 */
const ADULT_URL = 'https://glykofans.com/v1-1sp';
const MONEY_ROUTE = '/demo';
const LOGO_SRC = 'https://cdn.magicpatterns.com/uploads/wYhSTif3aDAkQiioEGYVzF/private_openrouter-generated-image-1785044673395.png';
const AVATAR_SRC = 'https://randomuser.me/api/portraits/women/44.jpg';
const BRAND = '#00AFF0';
type View = 'quiz' | 'blocked';
type Track = 'money' | 'adult';
type Option = {
  label: string;
  primary?: boolean;
  onSelect: () => void;
};

/** A single locked post in the background feed. */
function LockedPost({
  caption,
  date,
  likes,
  meta
}: {
  caption: string;
  date: string;
  likes: number;
  meta: React.ReactNode;
}) {
  return <article className="border-b border-[#e5e7eb] bg-white" data-id="element-674" data-mp="1hlfb0zviu4s">
      <div className="flex items-start gap-3 px-4 pt-4" data-id="element-675" data-mp="3ix1y03urdffy">
        <img src={AVATAR_SRC} alt="" aria-hidden="true" width={44} height={44} loading="lazy" decoding="async" className="h-11 w-11 rounded-full object-cover" data-id="element-676" data-mp="3iufhhepw3pll" />
        <div className="min-w-0 flex-1" data-id="element-677" data-mp="3dxp10qa6arzn">
          <div className="flex items-center justify-between gap-2" data-id="element-678" data-mp="3dwnn8e1rvre8">
            <span className="flex items-center gap-1 truncate" data-id="element-679" data-mp="1kfrq73lr3ai3">
              <span className="text-[15px] font-bold text-[#111]" data-id="element-680" data-mp="1fo9rynb43o0t">gracie</span>
              <span aria-hidden="true" data-id="element-681" data-mp="1fm6rtag8weid">🌻</span>
              <BadgeCheckIcon className="h-4 w-4 text-[#111]" data-id="element-682" data-mp="1apygy992z7h0" />
            </span>
            <span className="shrink-0 text-[13px] text-[#8a96a3]" data-id="element-683" data-mp="15qc2jwhinl6m">{date}</span>
          </div>
          <p className="text-[13.5px] text-[#8a96a3]" data-id="element-684" data-mp="23dc9lv5qxgvb">@graciehiggins</p>
        </div>
      </div>

      <p className="px-4 py-3 text-[15px] leading-snug text-[#111]" data-id="element-685" data-mp="1yer6nnz40j36">{caption}</p>

      {/* Locked media */}
      <div className="relative bg-[#f1f3f5]" data-id="element-686" data-mp="1tev9o8otxrx4">
        <div className="flex flex-col items-center justify-center px-4 py-10" data-id="element-687" data-mp="1tdlcdlpswnrj">
          <LockIcon className="h-14 w-14 text-[#c9ced4]" strokeWidth={1.5} aria-hidden="true" data-id="element-688" data-mp="1pd1euh1bfi4v" />
          <div className="mt-8 w-full rounded-lg border border-[#e2e5e9] p-3" data-id="element-689" data-mp="hxwqkxoa9usb">
            <div className="mb-3 flex items-center gap-1.5 text-[13px] text-[#8a96a3]" data-id="element-690" data-mp="e0qu8xwq6sbi">
              {meta}
            </div>
            <span className="block w-full rounded-full py-3 text-center text-[14px] font-bold uppercase tracking-wide text-white" style={{
            backgroundColor: BRAND
          }} data-id="element-691" data-mp="2tnl1q7v5lt57">
              Suscríbete para ver las publicaciones
            </span>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between px-4 py-3" data-id="element-692" data-mp="38d0o7bnnjcf3">
        <div className="flex items-center gap-4 text-[#8a96a3]" data-id="element-693" data-mp="38etswlepfmkg">
          <HeartIcon className="h-6 w-6" strokeWidth={1.6} data-id="element-694" data-mp="3da0v1osbsj79" />
          <MessageCircleIcon className="h-6 w-6" strokeWidth={1.6} data-id="element-695" data-mp="3hcnsc57rmwbu" />
        </div>
        <BookmarkIcon className="h-6 w-6 text-[#8a96a3]" strokeWidth={1.6} data-id="element-696" data-mp="3mca5yfriael6" />
      </div>
      <p className="px-4 pb-4 text-[14px] text-[#111]" data-id="element-697" data-mp="3m9w6zvtpgi8c">
        <span className="font-bold" data-id="element-698" data-mp="1n0fykv76klhn">{likes}</span> likes
      </p>
    </article>;
}
export function QuizPage({
  onComplete
}: {
  /**
   * Called when the visitor finishes the "money" branch. When the quiz is used
   * as an inline gate (e.g. /PD1 renders the quiz before the lander), the
   * parent uses this to unlock the page in place. When omitted — the standalone
   * /quiz route — the quiz navigates to the lander itself.
   */
  onComplete?: () => void;
} = {}) {
  const navigate = useNavigate();
  const {
    trackCustomEvent,
    trackCustomEventTo
  } = useMetaPixel();
  const [view, setView] = useState<View>('quiz');
  const [step, setStep] = useState(1);
  // Which question set the visitor is in. Everyone starts on the money track
  // (age check + Q1-Q3); answering "no" at any point moves them onto the
  // exclusive-content questions instead of dropping them from the funnel.
  const [track, setTrack] = useState<Track>('money');
  const goMoney = () => {
    // Custom conversion for visitors who opt in to the presentation — fired to
    // every dataset configured for this route (same trio as /demo).
    trackCustomEvent('Funciono');
    for (const pixelId of getActiveCtaPixelIds()) {
      trackCustomEventTo(pixelId, 'Funciono');
    }
    if (onComplete) onComplete();else navigate(MONEY_ROUTE);
  };
  const goAdult = () => {
    window.location.href = ADULT_URL;
  };
  const next = () => setStep(s => s + 1);
  // Move the visitor onto the exclusive-content questions, restarting the step
  // count so they always see that track from its first question.
  const switchToAdult = () => {
    setTrack('adult');
    setStep(2);
  };

  // ---- Modal content ------------------------------------------------------
  let title = '';
  let subtitle: string | null = null;
  let options: Option[] = [];
  if (step === 1) {
    title = 'Verificación de edad';
    subtitle = 'Antes de continuar, confirma tu edad:';
    options = [{
      label: 'Sí, tengo 18 años o más',
      primary: true,
      onSelect: next
    }, {
      label: 'No, soy menor de 18 años',
      onSelect: () => setView('blocked')
    }];
  } else if (track === 'adult') {
    // ---- EXCLUSIVE-CONTENT TRACK ----
    // Reached when the visitor says "no" on any money question. Steps are
    // counted from whenever they switched over (see switchToAdult).
    if (step === 2) {
      title = '¿Qué tipo de contenido exclusivo prefieres?';
      options = [{
        label: 'Fotos exclusivas',
        onSelect: next
      }, {
        label: 'Videos privados',
        onSelect: next
      }, {
        label: 'Contenido nuevo cada semana',
        onSelect: next
      }, {
        label: 'Quiero ver todas las opciones',
        onSelect: next
      }];
    } else if (step === 3) {
      title = '¿Qué nivel de contenido estás buscando?';
      options = [{
        label: 'Sensual y sugerente',
        onSelect: next
      }, {
        label: 'Más atrevido',
        onSelect: next
      }, {
        label: 'Contenido completo y sin censura',
        onSelect: next
      }];
    } else {
      title = '¿Quieres acceder ahora al contenido exclusivo?';
      options = [{
        label: 'Sí, mostrarme el contenido',
        primary: true,
        onSelect: goAdult
      }, {
        label: 'Quiero conocer más primero',
        onSelect: goAdult
      }];
    }
  } else if (step === 2) {
    // Q1 — "no" moves the visitor onto the exclusive-content questions.
    title = '¿Te gustaría salir con mujeres que hoy sentís que están fuera de tu liga?';
    options = [{
      label: '✅ Sí, obvio.',
      onSelect: next
    }, {
      label: '❌ Nah, estoy bien así.',
      onSelect: switchToAdult
    }];
  } else if (step === 3) {
    // Q2
    title = 'Seamos sinceros… ¿pensás que tener más plata y libertad te ayudaría con las mujeres?';
    options = [{
      label: '✅ Sí, seguro.',
      onSelect: next
    }, {
      label: '❌ No, ni ahí.',
      onSelect: switchToAdult
    }];
  } else {
    // Q3 — final confirmation into the presentation.
    title = 'Si hubiera una forma simple de aprender a ganar plata por internet, ¿te gustaría verla?';
    options = [{
      label: '✅ Sí, mostrame.',
      primary: true,
      onSelect: goMoney
    }, {
      label: '❌ No me interesa.',
      onSelect: switchToAdult
    }];
  }
  return <div className="min-h-screen w-full bg-white font-['system-ui','-apple-system','BlinkMacSystemFont','Segoe_UI','Roboto','Helvetica','Arial','sans-serif']" data-id="element-699" data-mp="27gsd6jcy5xnu">
      {/* ============== BACKGROUND FEED (decorative, non-interactive) ============== */}
      <div aria-hidden="true" className="pointer-events-none select-none" data-id="element-700" data-mp="2cett4vf2tgv3">
        {/* Brand header */}
        <header className="sticky top-0 z-10 border-b border-[#e5e7eb] bg-white" data-id="element-701" data-mp="2r3q7tbbn1s4o">
          <div className="mx-auto flex max-w-[560px] items-center justify-center px-4 py-2" data-id="element-702" data-mp="2m5f0b78tftpm">
            <span className="block h-10 w-[190px] overflow-hidden" data-id="element-703" data-mp="304xtmtwyx0hv">
              <img src={LOGO_SRC} alt="GlykoFans" width={190} height={190} decoding="async" {...{
              fetchPriority: 'high'
            } as any} className="h-[190px] w-[190px] object-contain -my-[75px]" data-id="element-704" data-mp="3044vghc2rjbc" />
            </span>
          </div>
        </header>

        <div className="mx-auto max-w-[560px]" data-id="element-705" data-mp="3cg71sc9t5m1x">
          {/* Creator bar */}
          <div className="flex items-center justify-between border-b border-[#e5e7eb] bg-white px-4 py-3" data-id="element-706" data-mp="3herza3xodrmo">
            <ChevronLeftIcon className="h-6 w-6 text-[#111]" data-id="element-707" data-mp="33nn7tp4go5nf" />
            <div className="text-center" data-id="element-708" data-mp="33l0zhn9qxm45">
              <span className="flex items-center justify-center gap-1" data-id="element-709" data-mp="37ndvvl2ls7o9">
                <span className="text-[19px] font-bold text-[#111]" data-id="element-710" data-mp="2toxort0twf5l">
                  gracie
                </span>
                <span data-id="element-711" data-mp="2yo0ulfomupem">🌻</span>
                <BadgeCheckIcon className="h-4 w-4 text-[#111]" data-id="element-712" data-mp="2ypbyrglzpz69" />
              </span>
              <span className="flex items-center justify-center gap-1.5 text-[14px] text-[#8a96a3]" data-id="element-713" data-mp="2kpaxw15q6rqs">
                <span className="h-2 w-2 rounded-full bg-[#22c55e]" data-id="element-714" data-mp="2oqcoxuphnxho" />
                Available now
              </span>
            </div>
            <Share2Icon className="h-6 w-6 text-[#111]" data-id="element-715" data-mp="ius7e6uc803r" />
          </div>

          <LockedPost date="Jul 15" caption="si supieras lo que subí anoche 🙈" likes={132} meta={<>
                <ImageIcon className="h-4 w-4" data-id="element-717" data-mp="npz2lgcia1n1" /> 1
              </>} data-id="element-716" data-mp="mxok5gx751zs" />
          <LockedPost date="Mar 14" caption="contenido nuevo cada semana 💕" likes={214} meta={<>
                <ImageIcon className="h-4 w-4" data-id="element-719" data-mp="dxjf63ifwkur" /> 1
                <span className="mx-1" data-id="element-720" data-mp="11qi21z2wnzdl">·</span>
                <VideoIcon className="h-4 w-4" data-id="element-721" data-mp="11nnmakuu72qi" /> 0:10
              </>} data-id="element-718" data-mp="91kmi3q2wgoj" />
        </div>
      </div>

      {/* ============== MODAL ============== */}
      <div role="dialog" aria-modal="true" aria-label={view === 'blocked' ? 'Acceso restringido' : title} className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 px-4 py-8 backdrop-blur-[3px] overflow-y-auto" data-id="element-722" data-mp="so8spcjdic1s">
        <div className="w-full max-w-[420px] rounded-2xl bg-white p-6 shadow-[0_20px_60px_rgba(0,0,0,0.35)] sm:p-7" data-id="element-723" data-mp="3hguufsgngg48">
          {/* -------- BLOCKED (under 18) -------- */}
          {view === 'blocked' && <div className="text-center" data-id="element-724" data-mp="34wz22utzcc8l">
              <span className="mx-auto mb-4 inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#fee2e2]" data-id="element-725" data-mp="350d2hxj32bde">
                <ShieldAlertIcon className="h-7 w-7 text-[#dc2626]" data-id="element-726" data-mp="3ewg4n3dxqa0c" />
              </span>
              <h2 className="text-[19px] font-bold leading-snug text-[#111]" data-id="element-727" data-mp="2r6lu4sh38xj9">
                Acceso restringido
              </h2>
              <p className="mt-3 text-[15px] leading-relaxed text-[#65676b]" data-id="element-728" data-mp="2m8akoiupgp1k">
                Este contenido está disponible únicamente para mayores de 18
                años.
              </p>
            </div>}

          {/* -------- QUESTIONS -------- */}
          {view === 'quiz' && <div data-id="element-729" data-mp="28f2rimqz7lhr">
              {step === 1 && <div className="mb-4 flex justify-center" data-id="element-730" data-mp="28drnqmxj6dqn">
                  <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#e6f6fe]" data-id="element-731" data-mp="23g86de5jdkuh">
                    <LockIcon className="h-7 w-7" style={{
                color: BRAND
              }} data-id="element-732" data-mp="2t4ww16732lbq" />
                  </span>
                </div>}

              <h2 className={`text-[18px] font-bold leading-snug text-[#111] ${step === 1 ? 'text-center' : ''}`} data-id="element-733" data-mp="31xqqwbz0jtap">
                {title}
              </h2>
              {subtitle && <p className={`mt-2 text-[14.5px] leading-relaxed text-[#65676b] ${step === 1 ? 'text-center' : ''}`} data-id="element-734" data-mp="2a72qx392stym">
                  {subtitle}
                </p>}

              <div className="mt-5 space-y-2.5" data-id="element-735" data-mp="2j67oo7ctcj9q">
                {options.map(opt => opt.primary ? <button key={opt.label} type="button" onClick={opt.onSelect} style={{
              backgroundColor: BRAND
            }} className="w-full rounded-full px-5 py-3.5 text-[15px] font-bold text-white transition-opacity duration-150 hover:opacity-90 active:scale-[0.99]" data-id="element-736" data-mp="2o3zf0wxt7xbt">
                      {opt.label}
                    </button> : <button key={opt.label} type="button" onClick={opt.onSelect} className="w-full rounded-full border border-[#dcdfe3] px-5 py-3.5 text-[15px] font-semibold text-[#111] transition-colors duration-150 hover:border-[#00AFF0] hover:bg-[#e6f6fe] hover:text-[#0284c7] active:scale-[0.99]" data-id="element-737" data-mp="1e6hsoiyyczpn">
                      {opt.label}
                    </button>)}
              </div>

              {step === 1 && <p className="mt-5 text-center text-[11.5px] leading-relaxed text-[#9aa0a6]" data-id="element-738" data-mp="2pnraz26chlpd">
                  Al continuar confirmas que eres mayor de edad en tu
                  jurisdicción y aceptas ver contenido destinado a adultos.
                </p>}
            </div>}
        </div>
      </div>
    </div>;
}